'use client';

import { useMicrocopy } from '@lib/cms/microcopy/client';
import { useCountdown } from '@lib/hooks/useCountdown';
import { Duration } from 'date-fns';
import { formatDurationAsDays, interpolateStringWithData } from 'utils';
import { Sale } from './types';

const NUMBER_OF_ZEROES = 2;

const zeroPad = (num: number) => String(num).padStart(NUMBER_OF_ZEROES, '0');

const formatCountdownDuration = (
  countdownDuration: Duration,
  locale: string
): string => {
  const days = countdownDuration?.days ?? 0;
  const hours = countdownDuration?.hours ?? 0;
  const minutes = countdownDuration?.minutes ?? 0;

  if (days > 0) {
    return formatDurationAsDays(countdownDuration, locale);
  }

  return `${zeroPad(hours)}h ${zeroPad(minutes)}m`;
};

type ProductCardSaleOverlayProps = {
  sale: Sale;
  locale: string;
  isFlashDealActive?: boolean;
};

export const ProductCardSaleOverlay = ({
  sale,
  locale,
  isFlashDealActive,
}: ProductCardSaleOverlayProps) => {
  const microcopy = useMicrocopy('product-card', {
    'product-card.sale-ends': 'Sale ends {{time}}',
    'product-card.sale-starts': 'Sale in {{time}}',
  });

  const countdownDate = isFlashDealActive ? sale.dates.ends : sale.dates.starts;
  const countdownDuration = useCountdown(countdownDate);

  if (!countdownDuration) {
    return null;
  }

  const countdownFormatted = formatCountdownDuration(countdownDuration, locale);
  const microcopyKey = isFlashDealActive
    ? 'product-card.sale-ends'
    : 'product-card.sale-starts';

  return (
    <div
      className="absolute inset-x-0 bottom-0 flex h-40 flex-col justify-end p-3"
      style={{
        background:
          'linear-gradient(207deg, rgba(208, 0, 0, 0) 60.63%, rgb(0, 0, 0) 99.36%)',
      }}
    >
      <div
        className="inline-flex w-fit items-center justify-center rounded px-1 backdrop-blur-[10px]"
        style={{ backgroundColor: 'rgba(255, 237, 236, 0.15)' }}
        data-test="product-card-sale-overlay"
        suppressHydrationWarning
      >
        <span className="text-xs font-normal tracking-tight text-white">
          {interpolateStringWithData(microcopy[microcopyKey], {
            time: countdownFormatted,
          })}
        </span>
      </div>
    </div>
  );
};
